// src/services/cleLecture.js — LA CLÉ DE LECTURE DES DOCUMENTS.
//
// Vécu 28.09 : « je ne veux plus voir des liens comme ça pour accéder à mes
// documents : …/documents/5271/preview?token=eyJhbGci… ». Un <img>, un
// <iframe>, un onglet ou un lien de téléchargement n'envoient pas d'en-tête
// d'authentification : on recopiait donc le JETON DE SESSION dans l'adresse
// — la session entière, sept jours, toutes les actions, dans l'historique du
// navigateur et dans chaque copier-coller.
//
// Le serveur délivre désormais une CLÉ DE LECTURE (POST /auth/cle-lecture) :
// 30 minutes, lecture seule, acceptée seulement dans l'adresse d'un fichier.
// Ce module la garde, la renouvelle avant qu'elle expire, et l'appose aux
// liens (`avecCle`). Un lien reste donc un lien — mais il ne vaut plus une
// session, et il meurt en une demi-heure.
//
// Trois garanties :
//   • `avecCle` est SYNCHRONE (les adresses se construisent au rendu) : il
//     rend la clé en main, et en demande une nouvelle en fond si elle vieillit ;
//   • un lien rendu avant l'arrivée de la clé, ou gardé à l'écran plus
//     longtemps qu'elle, est CORRIGÉ au clic (écoute globale, une seule) ;
//   • `useCleLecture()` fait re-rendre ce qui affiche une image quand la clé
//     arrive ou change — sinon une photo rendue sans clé resterait aux
//     initiales.
import { useEffect, useState } from 'react';

const BASE = 'https://besoins-tat-production.up.railway.app';
const MARGE_MS = 10 * 60 * 1000;          // renouvelée quand il reste 10 minutes
const PARAM = 'cle';

let cle = null;
let expireLe = 0;
let cleDe = null;          // le compte pour lequel la clé a été délivrée
let enVol = null;
const abonnes = new Set();

const jetonSession = () => { try { return localStorage.getItem('token'); } catch (e) { return null; } };
// Le compte de la session en cours, lu dans le jeton (affichage/comparaison
// seulement — le contrôle est au serveur). Une clé délivrée pour un autre
// compte (déconnexion puis connexion sous un autre nom) n'est JAMAIS reprise.
const compteSession = () => {
  try {
    const charge = (jetonSession() || '').split('.')[1];
    if (!charge) return null;
    const json = JSON.parse(decodeURIComponent(escape(atob(charge.replace(/-/g, '+').replace(/_/g, '/')))));
    return json.userId ?? null;
  } catch (e) { return null; }
};
const prevenir = () => abonnes.forEach(f => { try { f(cle); } catch (e) { /* un abonné ne casse pas les autres */ } });

/** Demande une clé neuve au serveur (une seule demande à la fois). */
export function rafraichirCle() {
  if (enVol) return enVol;
  const jeton = jetonSession();
  if (!jeton) return Promise.resolve(null);
  const pour = compteSession();
  enVol = fetch(`${BASE}/auth/cle-lecture`, { method: 'POST', headers: { Authorization: `Bearer ${jeton}` } })
    .then(r => (r.ok ? r.json() : null))
    .then(j => {
      if (j && j.cle && compteSession() === pour) {
        cle = j.cle;
        cleDe = pour;
        expireLe = Date.parse(j.expire_le) || (Date.now() + 30 * 60 * 1000);
        prevenir();
      }
      return cle;
    })
    .catch(() => cle)
    .finally(() => { enVol = null; });
  return enVol;
}

/** La clé en main (ou null au tout premier rendu) ; en redemande une en fond si elle vieillit. */
export function cleCourante() {
  if (cle && (!jetonSession() || compteSession() !== cleDe)) { cle = null; expireLe = 0; cleDe = null; }
  if (!cle || Date.now() > expireLe - MARGE_MS) rafraichirCle();
  return cle && Date.now() < expireLe ? cle : null;
}

/** Remplace (ou ajoute) la clé dans une adresse de notre API. */
function poserCle(url, valeur) {
  try {
    const u = new URL(url, BASE);
    u.searchParams.delete('token');           // l'ancien jeton de session ne voyage plus
    if (valeur) u.searchParams.set(PARAM, valeur); else u.searchParams.set(PARAM, '');
    return u.toString();
  } catch (e) { return url; }
}

/**
 * L'adresse d'un fichier servi par notre API, prête à ouvrir dans un onglet,
 * un <img>, un <iframe> ou un lien de téléchargement. Sans clé encore en main,
 * le paramètre est posé VIDE : le clic le complète (voir installerCleLecture).
 */
export function avecCle(url) {
  if (!url || typeof url !== 'string') return url;
  if (!url.startsWith(BASE) && !url.startsWith('/')) return url;   // une adresse d'ailleurs ne reçoit rien
  return poserCle(url.startsWith('/') ? `${BASE}${url}` : url, cleCourante());
}

/** Re-rend le composant quand la clé arrive ou change (images, aperçus). */
export function useCleLecture() {
  const [valeur, setValeur] = useState(() => cleCourante());
  useEffect(() => {
    abonnes.add(setValeur);
    if (!cle) rafraichirCle();
    return () => { abonnes.delete(setValeur); };
  }, []);
  return valeur;
}

/** À l'ouverture de l'application : clé demandée, renouvelée, et liens corrigés au clic. */
export function installerCleLecture() {
  if (typeof window === 'undefined' || window.__cleLectureInstallee) return;
  window.__cleLectureInstallee = true;
  if (jetonSession()) rafraichirCle();
  setInterval(() => { if (jetonSession() && Date.now() > expireLe - MARGE_MS) rafraichirCle(); }, 60 * 1000);
  // Un lien rendu il y a une heure porte une clé morte : on met la clé du
  // moment AVANT que le navigateur ne suive le lien. Phase de capture, pour
  // passer avant tout gestionnaire qui ouvrirait l'onglet lui-même.
  const corriger = (e) => {
    const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a) return;
    const href = a.getAttribute('href') || '';
    if (!href.startsWith(BASE) || !href.includes(`${PARAM}=`)) return;
    const c = cleCourante();
    if (c) a.setAttribute('href', poserCle(href, c));
  };
  document.addEventListener('click', corriger, true);
  document.addEventListener('auxclick', corriger, true);
}

/** À la déconnexion : la clé part avec la session. */
export function oublierCle() { cle = null; expireLe = 0; cleDe = null; prevenir(); }
